import React, { useCallback, useState } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";

import { faCheckCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { environment } from "~/models";
import { toast } from "~/ui";
import Menu from "~/ui/menu";

import { type AbstractAction, ActionButton } from "./shared";

import type { EndMeeting_note$key } from "~/__generated__/EndMeeting_note.graphql";
import type { EndMeetingMutation } from "~/__generated__/EndMeetingMutation.graphql";

import suggestedActionsStyles from "./SuggestedActions.module.css";

const ACTION_ID = "end-meeting" as const;

const noteFragment = graphql`
	fragment EndMeeting_note on Note {
		id
		event {
			isOrganizer
		}
		stream {
			isMeetingFeedbackPromptEnabled
		}
	}
`;

const endMeetingMutation = graphql`
	mutation EndMeetingMutation($noteId: ID!) {
		noteEndMeeting(noteId: $noteId) {
			note {
				finished
				finishedAt
			}
		}
	}
`;

export interface EndMeetingProps {
	note: EndMeeting_note$key;
}

function useEndMeeting({ note: noteKey }: EndMeetingProps) {
	const [loading, setLoading] = useState(false);
	const note = useFragment(noteFragment, noteKey);

	const onEndMeeting = useCallback(() => {
		if (loading) return;

		setLoading(true);

		commitMutation<EndMeetingMutation>(environment, {
			mutation: endMeetingMutation,
			variables: { noteId: note.id },
			onCompleted: ({ noteEndMeeting }, err) => {
				setLoading(false);

				if (err?.length || !noteEndMeeting) {
					toast.error(err?.[0]?.message || "Failed to end meeting");
				}
			},
		});
	}, [loading, note.id]);

	return { onEndMeeting, loading };
}

function Button(props: EndMeetingProps) {
	const { onEndMeeting, loading } = useEndMeeting(props);

	return (
		<ActionButton actionId={ACTION_ID} onClick={onEndMeeting} disabled={loading}>
			<FontAwesomeIcon
				icon={faCheckCircle}
				className={suggestedActionsStyles.actionButtonIcon}
				size="sm"
				color="var(--vividRoyal)"
			/>
			End meeting
		</ActionButton>
	);
}

function MenuItem(props: EndMeetingProps) {
	const { onEndMeeting, loading } = useEndMeeting(props);

	return (
		<Menu.Item
			icon={faCheckCircle}
			iconProps={{ color: "var(--vividRoyal)" }}
			onSelect={onEndMeeting}
			disabled={loading}
		>
			End meeting
		</Menu.Item>
	);
}

export default {
	id: ACTION_ID,
	button: Button,
	menuItem: MenuItem,
} satisfies AbstractAction;
